// CsvParser.ts
import { CsvErrorCode, CsvError, ParseCsvOptions, ParseCsvResult, HeaderSpecItem, HeaderCheckResult } from "@/util/import/CsvTypes";

// CsvParser.ts
// csvファイルのインポート時のファイル読み込みの共通処理を提供する
// 内容のエラーハンドリングは機能の仕様に依存するので呼び出し元で行う
// 呼び出し元から関数を渡して内部で処理してもいいがとっ散らかるだけなので止めた
//
// csvファイルのheaderとbodyの読み取りと、機能の使用に依存しない共通エラー処理をこの層で行う。
// string[][]で返すモードとobject[]で返すモードを使い分けられる
// 基本はobject[]で返すモードでいいハズ
//
// 仕様:ファイル全体
// - 仕様
// ・カラム内での改行は許容していない
// ・以下のようなフォーマットに対応可能
// カラムのダブルクォート囲み
// ex. "abc""xyz"->abc"xyz
// ex. "abc,xyz"->abc,xyz
//
// - エラー処理
// -- 共通
// ・ファイル読み込み失敗
// ・ファイルが空(0行)
// ・行解析中にダブルクォートが閉じられずに終了した行の存在
// ・行解析中にパースに失敗の例外が発生したとき
// -- オプション
// ・拡張子がcsvでない
// ・ファイルサイズが指定サイズより大きい

// 仕様:ヘッダ部
// ファイル1行目をヘッダ部として解釈する
// ヘッダ部はtrimされる
// - エラー処理
// -- オプション(headerSpec指定時)
// ・必須ヘッダの欠損
// ・ヘッダの重複
// ※認知していないヘッダの重複は警告どまり(読み捨てるので影響がないため)

// 仕様：ボディ部
// ファイル2行目以降をボディ部として解釈する(1行1レコード)
// 空行は読み飛ばす(エラーにしない)
// ボディ行のカラム数とヘッダ数が一致していなくてもエラーにはしない
// 呼び出し元の内容チェックで行う(mapRowのなかでundefindなり空文字なりを入れる)

// TODO
// 21.2にメッセージマクロが導入されたら
// この中で出す共通ErrorとメッセージIdの紐付けMapを作る

// How to Use
/*
// parseCsvAs(Objectの配列の返却を希望)

// 解析結果Object型定義
interface UserRow {
  name: string;
  age: number;
  email?: string;
}

// ヘッダ定義
const headerSpec: HeaderSpecItem[] = [
  { name: "名前", required: true, trim: true, mapTo: "name" },
  { name: "年齢", required: true, trim: true, mapTo: "age" },
  { name: "メール", required: false, trim: true, mapTo: "email", defaultValue: "aaa@bbb.ccc", },
];

// 使用例
async function example() {
  // ファイル情報
  const file = new File(
    [
      `名前,年齢,メ～ル
山田太郎,30,taro@example.com
鈴木花子,25,hanako@example.com
佐藤次郎,40,jiro@example.com`,
    ],
    "sample.csv",
    { type: "text/csv" }
  );
  // UserRow[]の取得
  // mapRowでRecord<string, string | undefined>->UserRowを行う
  const result = await parseCsvAs<UserRow>(file, {
    headerSpec,
    mapRow: (row) => ({
      name: row.name ?? "",
      age: Number(row.age),
      email: row.email,
    }),
  });
}*/

/*
// parseCsv(string[]の配列の返却を希望)
// 使用例
async function example1() {
  // ファイル情報
  const file = new File(
    [
      `名前,年齢,メ～ル
山田太郎,30,taro@example.com
鈴木花子,25,hanako@example.com
佐藤次郎,40,jiro@example.com`,
    ],
    "sample.csv",
    { type: "text/csv" }
  );
  const result = await parseCsv(file, { asObject: false });
}*/

// メイン関数(外部公開)
export async function parseCsv<T = string[]>(
    file: File,
    options: ParseCsvOptions<T>
): Promise<ParseCsvResult<T>> {
    const internal = await _parseCsvInternal(file, options);

    const rows = internal.rows?.map((r) => ({
        lineNumber: r.lineNumber,
        row: r.row,
    }));

    return {
        ok: internal.ok,
        errors: internal.errors,
        headerInfo: internal.headerInfo,
        rows,
    };
}

// メイン関数(外部公開 object[])
export async function parseCsvAs<T>(
    file: File,
    options: ParseCsvOptions<T>
): Promise<ParseCsvResult<T>> {
    return parseCsv<T>(file, { ...options, asObject: true });
}

/**
 * データ読み込んで返すだけの関数
 * @param file ファイルオブジェクト
 * @param encoding エンコード
 * @returns
 */
export async function readFileData(file: File, encoding: string): Promise<string> {
    try {
        return await readFileText(file, encoding);
    } catch {
        return "";
    }
}

async function _parseCsvInternal<T>(
    file: File,
    options: ParseCsvOptions<T>
): Promise<ParseCsvResult<T>> {
    const {
        extension = false,
        encoding = "utf-8",
        maxFileSize,
        asObject = false,
        existHeader = true,
        headerSpec = [],
        skipEmptyRow = true,
        noCheckQuote = false,
        mapRow,
    } = options;

    // 前処理（ファイルチェック）
    const precheck = preCheckFile(file, extension, maxFileSize);
    if (!precheck[0]) {
        // ロジック上でfalse時はprecheck[1]がundefindでないことは保証している
        return { ok: false, errors: [precheck[1] as CsvError] };
    }

    // ファイル読み込み
    // 結果的にobjectとして持つのでメモリ上に全部読み込んでも問題と判断している
    let text: string;
    try {
        text = await readFileText(file, encoding);
    } catch {
        const error = {
            code: CsvErrorCode.INVALID_ENCODING,
            message: "文字コードが不正または読み込めません",
        };
        return { ok: false, errors: [error] };
    }

    // \r\n (CRLF) または \n (LF) が末尾にあれば削除する
    const trimText = text.replace(/\r?\n$/, '');

    // 行 split
    const lines = splitLines(trimText);

    // 行が存在しない
    if (text.length === 0 || lines.length === 0) {
        const error = {
            code: CsvErrorCode.EMPTY,
            message: "ファイルが空です",
        };
        return { ok: false, errors: [error] };
    }

    // ヘッダ解析
    let headerInfo: HeaderCheckResult = {
        duplicateHeaders: [],
        missingHeaders: [],
        normalizedHeader: [],
        warningHeaders: [],
    };
    const errors: CsvError[] = [];
    let bodyResult: ParseCsvResult<T> = { ok: false, errors: errors, rows: [] };

    if (existHeader) {
        // ヘッダ解析
        const headerResult = readAndValidateHeader(lines[0], headerSpec);
        if (headerResult.errors.length > 0) {
            return {
                ok: false,
                errors: headerResult.errors,
                headerInfo: headerResult.headerInfo,
            };
        }
        headerInfo = headerResult.headerInfo;

        // ボディ解析
        bodyResult = parseBodyLines<T>(
            lines.slice(1),
            headerInfo.normalizedHeader,
            asObject,
            headerSpec,
            mapRow
        );
    } else {
        // headerなし
        // このケースはasObjectに非対応なので開発者に知らせるため、例外をスローする。
        if (asObject) {
            throw new Error(
                "Invalid configuration: The `asObject` option cannot be `true` when `existHeader` is `false`. Object key mapping requires a header row to be present."
            );
        }
        bodyResult = parseBodyLinesNoHeader(lines, noCheckQuote, skipEmptyRow) as ParseCsvResult<T>;
    }

    // failed..
    if (!bodyResult.ok) {
        return {
            ok: false,
            errors: bodyResult.errors,
            headerInfo: headerInfo,
        };
    }

    // succsess
    return {
        ok: true,
        errors: [],
        headerInfo: headerInfo,
        rows: bodyResult.rows,
    };
}

// 前チェック(!!!falseのときは必ずCsvErrorを返すこと!!!)
function preCheckFile(
    file: File,
    extension: boolean,
    maxFileSize?: number
): [boolean, CsvError?] {
    // ファイルサイズチェック
    if (maxFileSize && file.size > maxFileSize) {
        const error = {
            code: CsvErrorCode.FILE_TOO_LARGE,
            message: `ファイルサイズが大きすぎます (${file.size} bytes)`,
        };
        return [false, error];
    }

    // 拡張子チェック
    if (extension && !file.name.toLowerCase().endsWith(".csv")) {
        const error = {
            code: CsvErrorCode.FILE_NOT_CSV,
            message: "CSVファイルではありません",
        };
        return [false, error];
    }

    // 必要に応じて追加
    return [true, undefined];
}

// UTF-8/Shift-JIS 等で読み込む(encodingは明示的に指定)
async function readFileText(file: File, encoding: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("読み込み失敗"));
        reader.onload = () => resolve(reader.result as string);
        reader.readAsText(file, encoding);
    });
}

// 行 split
function splitLines(text: string): string[] {
    return text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
}

// 行 の CSV split (クオート対応)
// クオート閉じ忘れのときに例外を投げる
// 要素のトリムは呼び出し元で行う
function parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const c = line[i];

        if (c === '"') {
            if (inQuotes && line[i + 1] === '"') {
                // '"'をエスケープしていると判断している
                // ex. "aaa""aaa"->aaa"aaa
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (c === "," && !inQuotes) {
            // 区切り文字 and inQuotesなら例のようなフォーマットと判断するためこのif句には入らない
            // ex."aaa,bbb"->aaa,bbb
            result.push(current);
            current = "";
            continue;
        }

        current += c;
    }

    // parce error
    if (inQuotes) {
        throw {
            code: CsvErrorCode.QUOTE_NOT_CLOSED,
            message: "クオートが閉じられていません",
        };
    }

    result.push(current);
    return result;
}

// ヘッダの読み取り,検証
// ※不要なものが混じっていても良いとする(実害がないので)
function readAndValidateHeader(
    line: string,
    headerSpec: HeaderSpecItem[]
): {
    errors: CsvError[];
    headerInfo: HeaderCheckResult;
} {
    let header: string[];
    // header行の読み取り
    try {
        header = parseCsvLine(line).map((h) => h.trim());
    } catch (e: any) {
        return {
            errors: [
                { code: e.code ?? CsvErrorCode.PARSE_ERROR, message: e.message },
            ],
            headerInfo: {
                duplicateHeaders: [],
                missingHeaders: [],
                normalizedHeader: [],
            },
        };
    }

    const errors: CsvError[] = [];

    // 重複NG Check
    const duplicate = findDuplicates(header);
    const specHeaderNames = new Set(headerSpec.map((h) => h.name));
    const duplicateInSpec = duplicate.filter((d) => specHeaderNames.has(d));
    if (duplicateInSpec.length > 0) {
        errors.push({
            code: CsvErrorCode.HEADER_DUPLICATED,
            message: `ヘッダが重複しています: ${duplicateInSpec.join(", ")}`,
            detail: duplicateInSpec,
        });
    }

    // 認知してないヘッダが重複していたときは警告止まり
    const duplicateUnknown = duplicate.filter((d) => !specHeaderNames.has(d));

    // 必須NG Check
    const missing = headerSpec
        .filter((hs) => hs.required)
        .map((hs) => hs.name)
        .filter((name) => !header.includes(name));
    if (missing.length > 0) {
        errors.push({
            code: CsvErrorCode.HEADER_MISSING,
            message: `必須ヘッダが不足しています: ${missing.join(", ")}`,
            detail: missing,
        });
    }

    // result
    return {
        errors,
        headerInfo: {
            duplicateHeaders: duplicate,
            missingHeaders: missing,
            normalizedHeader: header,
            warningHeaders: duplicateUnknown,
        },
    };
}

// T[]の重複チェックを行います
// 重複していた項目のT[]を返します
function findDuplicates<T>(arr: T[]): T[] {
    const seen = new Set<T>(); // 発見済み
    const dup = new Set<T>(); // 重複
    arr.forEach((v) => {
        if (seen.has(v)) dup.add(v);
        else seen.add(v);
    });
    return [...dup]; // set->array
}

// ボディの読み取り、解析
function parseBodyLines<T>(
    lines: string[],
    header: string[],
    asObject: boolean,
    headerSpec: HeaderSpecItem[],
    mapRow?: (row: Record<string, string | undefined>) => T
): ParseCsvResult<T> {
    const errors: CsvError[] = [];
    const rows: { lineNumber: number; row: T }[] = [];
    const specMap = new Map(headerSpec.map((h) => [h.name, h]));

    // parse errorを途中でreturnするためforeachではなく純粋なforを使う
    for (let index = 0; index < lines.length; index++) {
        let cols: string[];
        const line = lines[index];

        // 空行の存在は問題ないものとして読み飛ばす
        if (line === "") continue;

        // 行の読み取り
        try {
            cols = parseCsvLine(line);
        } catch (e: any) {
            errors.push({
                code: e.code ?? CsvErrorCode.PARSE_ERROR,
                message: `行 ${index + 2} のパースに失敗しました: ${e.message}`,
                detail: [line],
            });
            return { ok: false, errors, rows: [] };
        }

        if (asObject) {
            const obj: Record<string, string | undefined> = {};

            for (let i = 0; i < headerSpec.length; i++) {
                const spec = headerSpec[i];
                const key = spec.mapTo ?? spec.name;
                const csvIndex = header.indexOf(spec.name);

                // CSV にあればその値、なければ defaultValue
                let value: string | undefined =
                    (csvIndex >= 0 ? cols[csvIndex] : undefined) ??
                    spec.defaultValue ??
                    undefined;

                // trim処理
                if (typeof value === "string" && spec.trim) value = value.trim();

                obj[key] = value;
            }

            const finalRow = mapRow ? mapRow(obj) : (obj as T);
            rows.push({ lineNumber: index + 2, row: finalRow });
        } else {
            const finalRow = cols.map((v, i) => {
                const spec = specMap.get(header[i]);
                return (spec?.trim ? v.trim() : v);
            }) as T;
            rows.push({ lineNumber: index + 2, row: finalRow });
        }
    }

    return { ok: true, errors: [], rows };
}

// ボディの読み取り、解析(ヘッダーなし)
function parseBodyLinesNoHeader(
    lines: string[],
    noCheckQuote: boolean,
    skipEmptyRow: boolean,
): ParseCsvResult<string[]> {
    const errors: CsvError[] = [];
    const rows: { lineNumber: number; row: string[] }[] = [];

    // parse errorを途中でreturnするためforeachではなく純粋なforを使う
    for (let index = 0; index < lines.length; index++) {
        let cols: string[];
        const line = lines[index];

        // 空行はスキップしたい場合のみスキップ
        if (line === "" && skipEmptyRow) continue;

        // 行の読み取り
        try {
            cols = noCheckQuote ? [line] : parseCsvLine(line);
            rows.push({ lineNumber: index + 1, row: cols });
        } catch (e: any) {
            errors.push({
                code: e.code ?? CsvErrorCode.PARSE_ERROR,
                message: `行 ${index + 1} のパースに失敗しました: ${e.message}`,
                detail: [line],
            });
            return { ok: false, errors, rows: [] };
        }
    }

    return { ok: true, errors: [], rows };
}